import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Box, Chip, Divider, Link, Typography } from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import { htmlAttr } from '../utils/htmlAttr';

// GitHub PR bodies embed screenshots as raw <img> tags, which react-markdown drops
// because it does not render raw HTML. We rewrite them into markdown image syntax,
// but only when the src is a GitHub attachment: a PR body is author-supplied text,
// so honouring an arbitrary src would let it point this page at any server.
const ATTACHMENT_IMAGE_PREFIX = 'https://github.com/user-attachments/';

const IMG_TAG = /<img\b[^>]*>/gi;

// Splits text into things we must leave alone (code, existing links/images, any HTML
// tag) and bare URLs. Only the bare URLs get wrapped, so a URL that is already the
// target of a link is not linked twice.
const CODE_OR_LINK_OR_URL =
  /(```[\s\S]*?```|`[^`]*`|!?\[[^\]]*\]\([^)]*\)|<[^>]+>)|(https?:\/\/[^\s<>()[\]"']+)/g;

// Sentence punctuation that follows a URL in prose is not part of the URL.
const TRAILING_PUNCTUATION = /[.,;:!?]+$/;

/** Converts GitHub-flavoured extras (raw attachment <img> tags, bare URLs) to plain markdown. */
export function preprocessMarkdown(source: string): string {
  const withImages = source.replace(IMG_TAG, tag => {
    const src = htmlAttr(tag, 'src');
    if (!src || !src.startsWith(ATTACHMENT_IMAGE_PREFIX)) return tag;
    // ']' would terminate the alt text early and break the image syntax.
    const alt = (htmlAttr(tag, 'alt') || '').replace(/[[\]]/g, '');
    return '![' + alt + '](' + src + ')';
  });

  return withImages.replace(CODE_OR_LINK_OR_URL, (match, skip, url) => {
    if (skip) return match;
    const punctuation = TRAILING_PUNCTUATION.exec(url);
    const link = punctuation ? url.slice(0, -punctuation[0].length) : url;
    return '[' + link + '](' + link + ')' + (punctuation ? punctuation[0] : '');
  });
}

// Blocks space themselves from below only, so the first one still sits flush with the
// top of whatever contains the markdown without needing a :first-child override.
const headingSx = { fontWeight: 600, lineHeight: 1.4, mt: 2.5, mb: 1 };
const blockSx = { mt: 0, mb: 1.5, lineHeight: 1.6 };

const markdownComponents = {
  p: ({ children }: any) => <Typography variant="body2" sx={blockSx}>{children}</Typography>,
  h1: ({ children }: any) => <Typography sx={{ ...headingSx, fontSize: '1.1rem' }}>{children}</Typography>,
  h2: ({ children }: any) => <Typography sx={{ ...headingSx, fontSize: '1rem' }}>{children}</Typography>,
  h3: ({ children }: any) => <Typography sx={{ ...headingSx, fontSize: '0.9rem' }}>{children}</Typography>,
  a: ({ href, children }: any) => <Link href={href} target="_blank" rel="noopener">{children}</Link>,
  ul: ({ children }: any) => <Box component="ul" sx={{ ...blockSx, pl: 2.5 }}>{children}</Box>,
  ol: ({ children }: any) => <Box component="ol" sx={{ ...blockSx, pl: 2.5 }}>{children}</Box>,
  li: ({ children }: any) => <Typography component="li" variant="body2" sx={{ my: 0.5, lineHeight: 1.6 }}>{children}</Typography>,
  hr: () => <Divider sx={{ mt: 0, mb: 1.5 }} />,
  // Attachments on our private repo need a GitHub session, and browsers withhold GitHub's
  // SameSite=Lax session cookie from cross-site image loads, so an embedded <img> always
  // 404s. Following a link is a top-level navigation, which does carry the cookie.
  // `component="a"` keeps this an <a>, which is valid inside the paragraph these sit in.
  img: ({ src, alt }: any) => (
    <Chip
      component="a"
      href={src}
      target="_blank"
      rel="noopener"
      clickable
      size="small"
      icon={<ImageIcon />}
      label={alt || 'Screenshot'}
      sx={{ mr: 0.5, mb: 0.5 }}
    />
  ),
  code: ({ children }: any) => (
    <Typography
      component="code"
      variant="body2"
      sx={{ bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5, fontFamily: 'monospace' }}
    >
      {children}
    </Typography>
  ),
};

export default function MarkdownContent({ children }: { children: string }) {
  return <ReactMarkdown components={markdownComponents}>{preprocessMarkdown(children)}</ReactMarkdown>;
}
